// dev-mobile-header — drawer toggle + submenu accordion for the tablet/mobile
// header. One delegated listener on `document`, bound once, so the theme editor
// can add and remove rows without a rescan and a section re-render cannot stack
// duplicate handlers.
(() => {
  if (window.__devMobileHeaderBound) return;
  window.__devMobileHeaderBound = true;

  const ROOT = '.dev-mheader';

  const root = () => document.querySelector(ROOT);
  const isOpen = (el) => el.classList.contains('is-open');

  // The panel can be taller than the viewport, so the page behind it must not
  // scroll away underneath. `position: fixed` on <body> would jump the page to
  // the top, so the scroll position is banked and restored instead.
  let bankedScroll = 0;

  function lock() {
    bankedScroll = window.scrollY;
    document.documentElement.classList.add('dev-mheader-locked');
    document.body.style.top = `-${bankedScroll}px`;
  }

  function unlock() {
    document.documentElement.classList.remove('dev-mheader-locked');
    document.body.style.top = '';
    window.scrollTo(0, bankedScroll);
  }

  function closeAllRows(el) {
    el.querySelectorAll('.dev-nav-row.is-open').forEach((row) => {
      row.classList.remove('is-open');
      const btn = row.querySelector('[data-dev-nav-toggle]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }

  // How much room there actually is under the bar. See the note on
  // `--dev-mheader-avail` in the section's stylesheet for why the bar height
  // alone is not the answer.
  function measure(el) {
    const bar = el.querySelector('.dev-mheader__bar');
    if (!bar) return;
    const avail = Math.max(0, window.innerHeight - bar.getBoundingClientRect().bottom);
    el.style.setProperty('--dev-mheader-avail', `${avail}px`);
  }

  function setDrawer(el, open) {
    el.classList.toggle('is-open', open);
    const toggle = el.querySelector('[data-dev-mheader-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.setAttribute('aria-label', open ? toggle.dataset.labelClose : toggle.dataset.labelOpen);
    }
    if (open) {
      // Measured before the lock, while the page is still in its normal flow.
      measure(el);
      lock();
    } else {
      unlock();
      // Collapsed on close so reopening starts from the drawn Active state
      // rather than from whatever the customer left expanded.
      closeAllRows(el);
    }
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-dev-mheader-toggle]');
    if (toggle) {
      const el = toggle.closest(ROOT);
      if (el) {
        event.preventDefault();
        setDrawer(el, !isOpen(el));
      }
      return;
    }

    const rowToggle = event.target.closest('[data-dev-nav-toggle]');
    if (rowToggle) {
      const row = rowToggle.closest('.dev-nav-row');
      const el = rowToggle.closest(ROOT);
      if (!row) return;
      event.preventDefault();
      const willOpen = !row.classList.contains('is-open');
      // One at a time: the frame only ever draws a single row expanded, and two
      // open submenus would push the panel well past any phone viewport.
      if (el) closeAllRows(el);
      row.classList.toggle('is-open', willOpen);
      rowToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      return;
    }

    // Click outside the header closes the drawer.
    const el = root();
    if (el && isOpen(el) && !event.target.closest(ROOT)) setDrawer(el, false);
  });

  window.addEventListener('resize', () => {
    const el = root();
    if (el && isOpen(el)) measure(el);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const el = root();
    if (!el || !isOpen(el)) return;
    setDrawer(el, false);
    const toggle = el.querySelector('[data-dev-mheader-toggle]');
    if (toggle) toggle.focus();
  });

  // Crossing back up to desktop while the drawer is open would otherwise leave
  // the body scroll-locked against a header that is no longer rendered.
  const wide = window.matchMedia('(min-width: 1200px)');
  const onWide = (e) => {
    const el = root();
    if (e.matches && el && isOpen(el)) setDrawer(el, false);
  };
  if (wide.addEventListener) wide.addEventListener('change', onWide);
  else wide.addListener(onWide);

  // Shopify re-renders a section on edit, which drops `is-open` from the markup
  // but leaves the document scroll-locked.
  document.addEventListener('shopify:section:load', () => {
    const el = root();
    if (!el || !isOpen(el)) unlock();
  });
})();
